import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import {
  CheckCircle,
  Ban,
  RotateCcw,
  Trash2,
  UserPlus
} from 'lucide-react'
import { ConfirmModal, AlertModal } from '../../components/ConfirmModal'

interface Profile {
  id: string
  role: string
  status: string
  admin_level: number | null
  email: string
  full_name: string | null
  phone: string | null
  house_number: string | null
  street: string | null
  city: string | null
  postcode: string | null
  cv_file: string | null
  google_form_file: string | null
}

export function ClientsTab() {
  const [clients, setClients] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)

  const [confirmState, setConfirmState] = useState<{
    open: boolean
    title: string
    message: string
    action: () => void
  }>({
    open: false,
    title: '',
    message: '',
    action: () => {}
  })

  const [alert, setAlert] = useState<{
    open: boolean
    title: string
    message: string
    variant: 'success' | 'danger' | 'info'
  }>({
    open: false,
    title: '',
    message: '',
    variant: 'info'
  })

  useEffect(() => {
    loadClients()
  }, [])

  async function loadClients() {
    setLoading(true)

    const { data, error } = await supabase.rpc('get_all_profiles')

    if (error) {
      setAlert({
        open: true,
        title: 'Error',
        message: error.message,
        variant: 'danger'
      })
      setLoading(false)
      return
    }

    const onlyClients =
      data?.filter((u: Profile) => u.role === 'client') || []

    setClients(onlyClients)
    setLoading(false)
  }

  async function approve(id: string) {
    const { error } = await supabase
      .from('profiles')
      .update({ status: 'active' })
      .eq('id', id)

    if (error) {
      setAlert({
        open: true,
        title: 'Error',
        message: error.message,
        variant: 'danger'
      })
      return
    }

    loadClients()
  }

  async function suspend(id: string) {
    const { error } = await supabase
      .from('profiles')
      .update({ status: 'suspended' })
      .eq('id', id)

    if (error) {
      setAlert({
        open: true,
        title: 'Error',
        message: error.message,
        variant: 'danger'
      })
      return
    }

    loadClients()
  }

  async function promote(id: string) {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        role: 'admin',
        admin_level: 1,
        status: 'active'
      })
      .eq('id', id)
      .select()

    if (error) {
      setAlert({
        open: true,
        title: 'Error',
        message: error.message,
        variant: 'danger'
      })
      return
    }

    if (!data || data.length === 0) {
      setAlert({
        open: true,
        title: 'Error',
        message: 'No rows updated',
        variant: 'danger'
      })
      return
    }

    setAlert({
      open: true,
      title: 'Success',
      message: 'Promotion successful',
      variant: 'success'
    })

    loadClients()
  }

  async function removeUser(id: string) {
    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', id)

    if (error) {
      setAlert({
        open: true,
        title: 'Error',
        message: error.message,
        variant: 'danger'
      })
      return
    }

    loadClients()
  }

  if (loading) return <div className="p-8">Loading...</div>

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Client Management</h1>

      {clients.length === 0 ? (
        <div>No clients found</div>
      ) : (
        <div className="bg-white shadow rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Files
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {clients.map((c) => (
                  <tr key={c.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium">
  {c.full_name}
</div>
                      <div className="text-sm text-gray-500">
                        {c.email}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm">
                      <div>{c.phone}</div>
                      <div>
                        {[c.house_number, c.street, c.city, c.postcode]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          c.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : c.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>

                    <td className="px-6 py-4 space-x-3 text-sm">
                      {c.cv_file && (
                        <a
                          href={c.cv_file}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600"
                        >
                          CV
                        </a>
                      )}
                      {c.google_form_file && (
                        <a
                          href={c.google_form_file}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600"
                        >
                          Form
                        </a>
                      )}
                    </td>

                    <td className="px-6 py-4 text-right space-x-2">
                      {c.status === 'pending' && (
                        <button
                          onClick={() =>
                            setConfirmState({
                              open: true,
                              title: 'Approve Client',
                              message: 'Approve this client?',
                              action: () => approve(c.id)
                            })
                          }
                        >
                          <CheckCircle size={18} />
                        </button>
                      )}

                      {c.status === 'active' && (
                        <button
                          onClick={() =>
                            setConfirmState({
                              open: true,
                              title: 'Suspend Client',
                              message: 'Suspend this client?',
                              action: () => suspend(c.id)
                            })
                          }
                        >
                          <Ban size={18} />
                        </button>
                      )}

                      {c.status === 'suspended' && (
                        <button
                          onClick={() =>
                            setConfirmState({
                              open: true,
                              title: 'Restore Client',
                              message: 'Restore this client?',
                              action: () => approve(c.id)
                            })
                          }
                        >
                          <RotateCcw size={18} />
                        </button>
                      )}

                      <button
                        onClick={() =>
                          setConfirmState({
                            open: true,
                            title: 'Promote to Admin',
                            message: 'Promote this client to admin?',
                            action: () => promote(c.id)
                          })
                        }
                      >
                        <UserPlus size={18} />
                      </button>

                      <button
                        onClick={() =>
                          setConfirmState({
                            open: true,
                            title: 'Delete Client',
                            message: 'Delete this user?',
                            action: () => removeUser(c.id)
                          })
                        }
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={() => {
          confirmState.action()
          setConfirmState(prev => ({ ...prev, open: false }))
        }}
        onCancel={() =>
          setConfirmState(prev => ({ ...prev, open: false }))
        }
      />

      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        onClose={() =>
          setAlert(prev => ({ ...prev, open: false }))
        }
      />
    </div>
  )
}